import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { toast } from '@cyphex/ui';
import type { Uuid } from '@cyphex/types/common';
import type {
  CreateMessageActionInput,
  CreateSlashCommandInput,
  MessageAction,
  SlashCommand,
} from '@cyphex/types/platform';
import { platformApi } from '../api/platformApi';
import { PLATFORM_KEYS } from '../api/queryKeys';

const REGISTRY_STALE_MS = 5 * 60_000;

/**
 * The composer's autocomplete source.
 *
 * Long stale time and no refetch on focus: this list changes when an
 * administrator registers a command, which is roughly never, and it is read on
 * every `/` keystroke in every conversation. A chatty query here would be a
 * request per composer per tab.
 */
export function useCommands(): UseQueryResult<SlashCommand[]> {
  return useQuery({
    queryKey: PLATFORM_KEYS.commands(),
    queryFn: ({ signal }) => platformApi.listCommands(signal),
    staleTime: REGISTRY_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/** The "..." menu's app contributions. Same reasoning as useCommands. */
export function useMessageActions(): UseQueryResult<MessageAction[]> {
  return useQuery({
    queryKey: PLATFORM_KEYS.messageActions(),
    queryFn: ({ signal }) => platformApi.listMessageActions(signal),
    staleTime: REGISTRY_STALE_MS,
    refetchOnWindowFocus: false,
  });
}

/* ---------------------------------------------------------------------------
 * Management views — administrators only
 * ------------------------------------------------------------------------- */

export function useManagedCommands(): UseQueryResult<SlashCommand[]> {
  return useQuery({
    queryKey: PLATFORM_KEYS.managedCommands(),
    queryFn: ({ signal }) => platformApi.listManagedCommands(signal),
    staleTime: 60_000,
  });
}

export function useManagedMessageActions(): UseQueryResult<MessageAction[]> {
  return useQuery({
    queryKey: PLATFORM_KEYS.managedMessageActions(),
    queryFn: ({ signal }) => platformApi.listManagedMessageActions(signal),
    staleTime: 60_000,
  });
}

export function useCommandMutations(onSecret: (secret: string) => void): {
  create: (input: CreateSlashCommandInput) => void;
  setEnabled: (input: { id: Uuid; enabled: boolean }) => void;
  remove: (id: Uuid) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const refresh = (): void => {
    // Both trees: the management list AND the composer registry every member
    // reads. Invalidating only the former leaves `/deploy` missing from
    // autocomplete until the next full reload.
    void queryClient.invalidateQueries({ queryKey: PLATFORM_KEYS.managedCommands() });
    void queryClient.invalidateQueries({ queryKey: PLATFORM_KEYS.commands() });
  };

  const create = useMutation({
    mutationFn: (input: CreateSlashCommandInput) => platformApi.createCommand(input),
    onSuccess: (created) => {
      refresh();
      onSecret(created.signingSecret);
    },
    onError: (error: Error) => toast.error(error.message || 'That command was rejected.'),
  });

  const setEnabled = useMutation({
    mutationFn: (input: { id: Uuid; enabled: boolean }) =>
      platformApi.setCommandEnabled(input.id, input.enabled),
    onSuccess: refresh,
    onError: () => toast.error('That change did not save.'),
  });

  const remove = useMutation({
    mutationFn: (id: Uuid) => platformApi.deleteCommand(id),
    onSuccess: () => {
      refresh();
      toast.success('Command removed.');
    },
    onError: () => toast.error('That command could not be removed.'),
  });

  return {
    create: create.mutate,
    setEnabled: setEnabled.mutate,
    remove: remove.mutate,
    isPending: create.isPending || setEnabled.isPending || remove.isPending,
  };
}

export function useMessageActionMutations(onSecret: (secret: string) => void): {
  create: (input: CreateMessageActionInput) => void;
  remove: (id: Uuid) => void;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: PLATFORM_KEYS.managedMessageActions() });
    void queryClient.invalidateQueries({ queryKey: PLATFORM_KEYS.messageActions() });
  };

  const create = useMutation({
    mutationFn: (input: CreateMessageActionInput) => platformApi.createMessageAction(input),
    onSuccess: (created) => {
      refresh();
      onSecret(created.signingSecret);
    },
    onError: (error: Error) => toast.error(error.message || 'That action was rejected.'),
  });

  const remove = useMutation({
    mutationFn: (id: Uuid) => platformApi.deleteMessageAction(id),
    onSuccess: () => {
      refresh();
      toast.success('Action removed.');
    },
    onError: () => toast.error('That action could not be removed.'),
  });

  return { create: create.mutate, remove: remove.mutate, isPending: create.isPending || remove.isPending };
}
